/**
 * Ecran « Entrez votre NIP » (design 4.2) et, en mode `manager`, la
 * confirmation d'un responsable (catalogue de plus de 3 jours).
 *
 * Au toucher (pave) ou au clavier : chiffres, Retour arriere, Entree. Le NIP
 * ne vit que dans l'etat de cet ecran, et il est vide apres chaque essai.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PinPad, { PIN_MAX_LENGTH, PIN_MIN_LENGTH, PinDots } from './PinPad';
import { LOGIN_STATUS, loginMessage, verifyPinLogin } from './pinLogin';
import { CONNECTION } from './heartbeat';
import { getLockout, lockoutMessage } from '../store/pinLockout';
import { palette } from './theme';

export default function PinScreen({
    db, api, token, connection, terminal, appVersion, mode = 'login', terminalUnknown = false,
    onSuccess, onCancel, onTerminalStatus, verify = verifyPinLogin,
}) {
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const [shakeKey, setShakeKey] = useState(0);
    const [lockUntil, setLockUntil] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    const manager = mode === 'manager';

    const locked = lockUntil !== null && now < lockUntil;

    const showLock = useCallback((lock) => {
        if (lock && lock.locked) {
            setLockUntil(lock.until);
            setNow(Date.now());
        }
    }, []);

    // Blocage deja en cours (rechargement, redemarrage) : affiche des l'ouverture.
    useEffect(() => {
        let cancelled = false;
        getLockout(db).then((lock) => { if (!cancelled) showLock(lock); }).catch(() => {});
        return () => { cancelled = true; };
    }, [db, showLock]);

    useEffect(() => {
        if (lockUntil === null) return undefined;
        const id = setInterval(() => {
            const current = Date.now();
            setNow(current);
            if (current >= lockUntil) {
                setLockUntil(null);
                setMessage(null);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [lockUntil]);

    const submit = useCallback(async () => {
        if (busy || locked || pin.length < PIN_MIN_LENGTH) return;
        const entered = pin;
        setPin('');
        setBusy(true);
        setMessage(null);
        let result;
        try {
            result = await verify({
                db, api, token, pin: entered,
                offline: connection === CONNECTION.OFFLINE,
                requireManager: manager,
            });
        } catch (_error) {
            result = { status: 'error' };
        }
        setBusy(false);
        switch (result.status) {
            case LOGIN_STATUS.OK:
                onSuccess(result);
                return;
            case LOGIN_STATUS.SUSPENDED:
                onTerminalStatus('DISABLED');
                return;
            case LOGIN_STATUS.REVOKED:
                onTerminalStatus('REVOKED');
                return;
            case LOGIN_STATUS.LOCKED:
                showLock(result.lock);
                return;
            default:
                break;
        }
        setShakeKey((k) => k + 1);
        setMessage(loginMessage(result) || 'La vérification n\'a pas abouti. Réessayez.');
        if (result.lock) showLock(result.lock);
    }, [busy, locked, pin, verify, db, api, token, connection, manager, onSuccess, onTerminalStatus, showLock]);

    const addDigit = useCallback((d) => {
        setPin((current) => (current.length >= PIN_MAX_LENGTH ? current : current + d));
    }, []);
    const backspace = useCallback(() => setPin((current) => current.slice(0, -1)), []);

    // Clavier : chiffres (rangee du haut ou pave numerique), Retour arriere, Entree.
    const keyHandler = useRef(null);
    keyHandler.current = (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (busy || locked) return;
        if (/^[0-9]$/.test(event.key)) {
            event.preventDefault();
            addDigit(event.key);
        } else if (event.key === 'Backspace') {
            event.preventDefault();
            backspace();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            submit();
        } else if (event.key === 'Escape' && manager && onCancel) {
            event.preventDefault();
            onCancel();
        }
    };
    useEffect(() => {
        const listener = (event) => keyHandler.current(event);
        window.addEventListener('keydown', listener);
        return () => window.removeEventListener('keydown', listener);
    }, []);

    const lockText = locked ? lockoutMessage(lockUntil - now) : null;

    return (
        <Box
            data-testid={manager ? 'ecran-nip-responsable' : 'ecran-nip'}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center' }}
        >
            {!manager && terminal && (
                <Box sx={{ mb: 1 }}>
                    <Typography component="p" data-testid="poste-magasin" sx={{ fontSize: 22, fontWeight: 700, color: palette.text }}>
                        {terminal.store_name || `Magasin ${terminal.store_id}`}
                    </Typography>
                    <Typography data-testid="poste-nom" sx={{ fontSize: 18, color: palette.textSecondary }}>
                        {terminal.name}
                    </Typography>
                </Box>
            )}
            <Typography sx={{ fontSize: 18, color: palette.textSecondary }}>
                {manager ? 'Prix de plus de 3 jours : un responsable doit confirmer' : 'Caisse de secours'}
            </Typography>
            <Typography component="h1" sx={{ fontSize: 34, fontWeight: 800, mt: 0.5, color: palette.text }}>
                {manager ? 'NIP du responsable' : 'Entrez votre NIP'}
            </Typography>

            <PinDots length={pin.length} shakeKey={shakeKey} />

            <Box role="alert" data-testid="nip-message" sx={{ minHeight: 56, mb: 1, maxWidth: 640 }}>
                {lockText && (
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: palette.errorOnTint }}>{lockText}</Typography>
                )}
                {!lockText && message && (
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: palette.errorOnTint }}>{message}</Typography>
                )}
                {!lockText && !message && busy && (
                    <Typography sx={{ fontSize: 18, color: palette.textSecondary }}>Vérification…</Typography>
                )}
            </Box>

            <PinPad
                length={pin.length}
                disabled={busy || locked}
                onDigit={addDigit}
                onBackspace={backspace}
                onSubmit={submit}
            />

            {manager ? (
                <Button variant="outlined" onClick={onCancel} sx={{ mt: 3, minHeight: 64, px: 6 }}>Annuler</Button>
            ) : (
                <Typography sx={{ mt: 3, fontSize: 16, color: palette.textSecondary }}>
                    Nouveau sur ce poste ? Il faut se connecter une fois quand Internet fonctionne.
                </Typography>
            )}

            {terminalUnknown && (
                <Alert severity="error" sx={{ mt: 2, fontSize: 18 }}>
                    L&apos;ERP ne reconnaît plus ce poste. Appelez le responsable.
                </Alert>
            )}

            {!manager && (
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, color: palette.textMuted }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.successOnTint }}>
                        <CheckCircleIcon sx={{ fontSize: 18 }} aria-hidden="true" />
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Poste prêt</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14 }}>Version {appVersion}</Typography>
                </Box>
            )}
        </Box>
    );
}
